export const SunIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.4 1.4M11.55 11.55l1.4 1.4M3.05 12.95l1.4-1.4M11.55 4.45l1.4-1.4" />
  </svg>
);

export const MoonIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M6 2a6 6 0 1 0 8 8A5 5 0 0 1 6 2z" />
  </svg>
);

export const PaletteIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M8 1.5C4.4 1.5 1.5 4.4 1.5 8c0 3.6 2.9 6.5 6.5 6.5 1 0 1.5-.5 1.5-1.5 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.2 0-1 .5-1.5 1.5-1.5h1.5c1.7 0 3-1.3 3-3 0-2.7-3.4-5.1-6.5-4.6Z" />
    <circle cx="5" cy="6.5" r="0.7" fill="currentColor" />
    <circle cx="6.5" cy="4.5" r="0.7" fill="currentColor" />
    <circle cx="9.5" cy="4.5" r="0.7" fill="currentColor" />
    <circle cx="11" cy="6.5" r="0.7" fill="currentColor" />
  </svg>
);

interface SizedIcon {
  size?: number;
  title?: string;
}

/** Hand-drawn star · used for "perfect" mock chapters (100%). */
export const RadarStarIcon = ({ size = 18, title }: SizedIcon = {}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    role={title ? 'img' : undefined}
    aria-label={title}
    aria-hidden={title ? undefined : true}
    style={{ transform: 'rotate(-3deg)' }}
  >
    {title && <title>{title}</title>}
    {/* Slightly irregular 5-point star drawn with small jitter — mimics felt-pen stroke. */}
    <path d="M12 3.2 L14.4 9 L20.6 9.4 L15.7 13.6 L17.4 19.8 L12 16.3 L6.6 19.7 L8.3 13.6 L3.4 9.5 L9.6 9.1 Z" />
    {/* small sparkle dot for "glow" feel */}
    <circle cx="18.5" cy="5.4" r="0.7" fill="currentColor" />
    <circle cx="5.4" cy="6.4" r="0.5" fill="currentColor" />
  </svg>
);

/** Hand-drawn check · used for ≥65% (passing) chapters. */
export const RadarCheckIcon = ({ size = 18, title }: SizedIcon = {}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role={title ? 'img' : undefined}
    aria-label={title}
    aria-hidden={title ? undefined : true}
    style={{ transform: 'rotate(2deg)' }}
  >
    {title && <title>{title}</title>}
    {/* Two-stroke check: short up-stroke then long down-stroke, slight curve mimics hand draw. */}
    <path d="M4.5 13.2 Q 7 15.4 9.4 18.1" />
    <path d="M9.4 18.1 Q 13.5 12.8 19.5 5.6" />
  </svg>
);

/** Hand-drawn warning triangle · used for 40-65% (orange zone). */
export const RadarWarningIcon = ({ size = 18, title }: SizedIcon = {}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    role={title ? 'img' : undefined}
    aria-label={title}
    aria-hidden={title ? undefined : true}
    style={{ transform: 'rotate(-1deg)' }}
  >
    {title && <title>{title}</title>}
    {/* Triangle with slightly wobbly sides via control-point Q curves. */}
    <path d="M12 3.4 Q 12.4 3.4 12.7 4 L 21.2 19.4 Q 21.5 20.2 20.7 20.4 L 3.5 20.4 Q 2.6 20.3 3 19.4 L 11.4 4 Q 11.7 3.4 12 3.4 Z" />
    {/* Exclamation strokes inside — slight angle for "scribbled" feel. */}
    <path d="M11.95 9.4 L 12.05 14.4" strokeWidth="2.2" />
    <circle cx="12" cy="17.4" r="0.85" fill="currentColor" stroke="none" />
  </svg>
);

/** Hand-drawn cross · used for <40% (red zone). */
export const RadarCrossIcon = ({ size = 18, title }: SizedIcon = {}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    role={title ? 'img' : undefined}
    aria-label={title}
    aria-hidden={title ? undefined : true}
    style={{ transform: 'rotate(4deg)' }}
  >
    {title && <title>{title}</title>}
    {/* Two crossing strokes with light Q curves — felt-pen X. */}
    <path d="M5.4 5.6 Q 12 11.8 18.6 18.4" />
    <path d="M18.4 5.4 Q 12 11.8 5.6 18.6" />
  </svg>
);

/** Hand-drawn em-dash · used for "no data". */
export const RadarDashIcon = ({ size = 18, title }: SizedIcon = {}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    role={title ? 'img' : undefined}
    aria-label={title}
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M5.4 12.2 Q 12 11.6 18.6 12.3" />
  </svg>
);
